import type {
  ICreateOrder,
  IDefaultStructure,
  IDefaultStructureAdapted,
  IDeliveryAreaAdapted,
  IGift,
} from 'modules/create-order/model/create-order.dto'
import type { ICustomerByPhone } from 'modules/orders/model/orders.dto'
import type { IDeliveryArea } from 'shared/model/search-product.dto'
import { defineStore } from 'pinia'
import { formatDeliveryAreaLabel } from 'shared/lib/utils/format-values'

export const useOrderCreateStore = defineStore(
  'createOrderStore',
  () => {
    const nullCustomerInfo: ICustomerByPhone = {
      active_bonuses: 0,
      blocked_bonuses: 0,
      chips_info: {
        active: 0,
        blocked: 0,
        nearest_expiration_date: '01.01.0001',
        expiration_total: 0,
      },
      email: '',
      expiration_total: 0,
      first_name: '',
      middle_name: '',
      last_name: '',
      full_name: '',
      nearest_expiration_date: '01.01.0001',
      phone: '',
    }
    const customerInfo = reactive<ICustomerByPhone>(structuredClone(nullCustomerInfo))

    const accessToEnter = ref(false)

    const nullOrder: ICreateOrder = {
      city: {
        id: '',
        name: '',
      },
      comment: {
        author: '',
        text: '',
        type: 'customer',
      },
      coupon_codes: [],
      customer: {
        comment: '',
        company: {
          bic: '',
          contactPerson: '',
          iic: '',
          kbe: '',
          legalAddress: '',
          name: '',
          uin: '',
        },
        email: '',
        firstname: '',
        lastname: '',
        middlename: '',
        personType: 'individual',
        phone: '',
      },
      delivery: [{
        address: {
          apartment: '',
          entrance: '',
          floor: 0,
          house: '',
          street: '',
          coordinates: {
            latitude: 0,
            longitude: 0,
          },
          delivery_area: {
            id: '',
            name: '',
            price: 0,
            available_days: [],
          },
        },
        delivery_date: '',
        delivery_service: {
          id: '',
          name: '',
        },
        express: false,
        id: '',
        price: 0,
        subdivision: {
          id: '',
          name: '',
        },
        type: 'courier',
      }],
      id: '',
      items: [],
      move_to: '',
      payment: [{
        payed: false,
        sum: 0,
        type: '',
      }],
      phone: '',
      point_of_contact: '',
      summary: {
        additional_services: 0,
        base_price: 0,
        delivery: 0,
        discount: 0,
        gifts: 0,
        payed: 0,
        spent_bonuses: 0,
        total: 0,
        unpaid: 0,
      },
    }
    const order = reactive<ICreateOrder>(structuredClone(nullOrder))
    // final_price из карточки товара по product_id — в позициях заказа его нет
    const productFinalPrices = ref<Record<string, number>>({})

    function setProductFinalPrice(productId: string, finalPrice: number) {
      productFinalPrices.value[productId] = finalPrice
    }

    const cities = ref<IDefaultStructureAdapted[]>([])
    const subdivisions = ref<IDefaultStructureAdapted[]>([])
    const deliveryServices = ref<IDefaultStructureAdapted[]>([])
    const deliveryAreas = ref<IDeliveryAreaAdapted[]>([])

    // FUNCTIONS
    function setCustomerInfo(data: ICustomerByPhone) {
      Object.assign(customerInfo, data)
    }

    function resetCustomerInfo() {
      Object.assign(customerInfo, structuredClone(nullCustomerInfo))
    }

    function setCities(data: IDefaultStructure[]) {
      cities.value = data.map((item) => {
        return {
          label: item.name,
          value: item.id,
        }
      })
    }

    function setSubdivisions(data: IDefaultStructure[]) {
      subdivisions.value = data.map((item) => {
        return {
          label: item.name,
          value: item.id,
        }
      })
    }

    function resetSubdivisions() {
      subdivisions.value = []
    }

    function setDeliveryServices(data: IDefaultStructure[]) {
      deliveryServices.value = data.map((item) => {
        return {
          label: item.name,
          value: item.id,
        }
      })
    }

    function resetDeliveryServices() {
      deliveryServices.value = []
    }

    function setDeliveryAreas(data: IDeliveryArea[]) {
      deliveryAreas.value = data.map((area) => {
        return {
          label: formatDeliveryAreaLabel(area),
          value: area.id,
          real_value: area,
        }
      })
    }

    function resetDeliveryAreas() {
      deliveryAreas.value = []
    }

    function setOrder(data: ICreateOrder) {
      Object.assign(order, {
        ...data,
        items: data.items.map((item) => {
          if (Object.keys(item.gifts || {}).length) {
            const sortedGifts: IGift = {}
            for (const category in item.gifts)
              sortedGifts[category] = item.gifts[category].sort((a, b) => a.code.localeCompare(b.code))

            return {
              ...item,
              gifts: sortedGifts,
            }
          }
          return item
        }),
      })
    }

    function resetOrder() {
      Object.assign(order, structuredClone(nullOrder))
      productFinalPrices.value = {}
    }

    function resetAll() {
      accessToEnter.value = false
      resetCustomerInfo()
      resetOrder()
    }

    return {
      customerInfo,
      accessToEnter,
      order,
      productFinalPrices,
      setProductFinalPrice,
      cities,
      subdivisions,
      deliveryServices,
      deliveryAreas,
      setCustomerInfo,
      resetCustomerInfo,
      setCities,
      resetAll,
      setOrder,
      resetOrder,
      setSubdivisions,
      resetSubdivisions,
      setDeliveryServices,
      resetDeliveryServices,
      setDeliveryAreas,
      resetDeliveryAreas,
    }
  },
  {
    persist: {
      pick: ['accessToEnter', 'customerInfo', 'order', 'productFinalPrices'],
    },
  },
)
